// AnimatedPuppy — a custom SVG puppy character, hand-drawn with pure SVG + CSS keyframes.
// No external assets, no photos. Uses PawLink palette (terracotta + cream + forest).
export default function AnimatedPuppy({ size = 180 }) {
    return (
        <div
            data-testid="animated-puppy"
            className="puppy-stage relative"
            style={{ width: size, height: size }}
            aria-label="Cute animated puppy"
            role="img"
        >
            <svg viewBox="0 0 200 200" width={size} height={size} className="overflow-visible">
                {/* Ground shadow (breathes) */}
                <ellipse cx="100" cy="182" rx="46" ry="6" fill="rgba(45,42,38,0.14)" className="puppy-shadow" />

                {/* Whole puppy — gently hops/breathes */}
                <g className="puppy-hop">
                    {/* Back leg (peeking) */}
                    <ellipse cx="128" cy="160" rx="10" ry="14" fill="#E8B48A" />
                    <ellipse cx="128" cy="168" rx="8" ry="5" fill="#C9664D" />

                    {/* Tail — wags */}
                    <g className="puppy-tail" style={{ transformOrigin: "142px 132px" }}>
                        <path d="M142 132 C 160 118, 175 130, 172 148"
                              stroke="#E8B48A" strokeWidth="12" strokeLinecap="round" fill="none" />
                        <circle cx="171" cy="148" r="7" fill="#FFFFFF" />
                    </g>

                    {/* Body */}
                    <ellipse cx="100" cy="140" rx="42" ry="34" fill="#F2D6BC" />
                    <ellipse cx="100" cy="140" rx="42" ry="34" fill="url(#bodyShade)" opacity="0.6" />
                    {/* White chest patch */}
                    <ellipse cx="100" cy="152" rx="20" ry="18" fill="#FFFFFF" />

                    {/* Front paws (waving one) */}
                    <g>
                        <ellipse cx="82" cy="168" rx="10" ry="7" fill="#FFFFFF" />
                        <ellipse cx="82" cy="168" rx="10" ry="7" fill="#F2D6BC" opacity="0.2" />
                    </g>
                    {/* Right waving paw */}
                    <g className="puppy-paw-wave" style={{ transformOrigin: "118px 158px" }}>
                        <ellipse cx="118" cy="168" rx="10" ry="7" fill="#FFFFFF" />
                    </g>

                    {/* Head group — bobs happily */}
                    <g className="puppy-head" style={{ transformOrigin: "100px 100px" }}>
                        {/* Ears — flap independently */}
                        <g className="puppy-ear-l" style={{ transformOrigin: "72px 80px" }}>
                            <path d="M72 80 C 55 80, 48 108, 62 120 C 72 118, 78 100, 78 88 Z"
                                  fill="#C9664D" />
                            <path d="M72 88 C 62 92, 60 108, 68 116"
                                  stroke="#8F3F2A" strokeWidth="1.6" fill="none" opacity="0.4" />
                        </g>
                        <g className="puppy-ear-r" style={{ transformOrigin: "128px 80px" }}>
                            <path d="M128 80 C 145 80, 152 108, 138 120 C 128 118, 122 100, 122 88 Z"
                                  fill="#C9664D" />
                            <path d="M128 88 C 138 92, 140 108, 132 116"
                                  stroke="#8F3F2A" strokeWidth="1.6" fill="none" opacity="0.4" />
                        </g>

                        {/* Head */}
                        <circle cx="100" cy="100" r="38" fill="#F2D6BC" />
                        {/* Warm cheek shade */}
                        <circle cx="100" cy="105" r="38" fill="url(#headShade)" opacity="0.55" />
                        {/* Snout */}
                        <ellipse cx="100" cy="118" rx="22" ry="17" fill="#FFFFFF" />
                        {/* Nose */}
                        <ellipse cx="100" cy="107" rx="6" ry="4.5" fill="#2D2A26" className="puppy-nose" />
                        {/* Nose shine */}
                        <ellipse cx="98.5" cy="105.5" rx="1.6" ry="1" fill="#FFFFFF" opacity="0.8" />
                        {/* Mouth line */}
                        <path d="M100 112 Q 100 120, 92 122" stroke="#2D2A26" strokeWidth="1.8" fill="none" strokeLinecap="round" />
                        <path d="M100 112 Q 100 120, 108 122" stroke="#2D2A26" strokeWidth="1.8" fill="none" strokeLinecap="round" />
                        {/* Tongue */}
                        <g className="puppy-tongue" style={{ transformOrigin: "100px 122px" }}>
                            <path d="M96 122 Q 100 132, 104 122 Z" fill="#E07A5F" />
                            <path d="M100 124 L 100 130" stroke="#B95B45" strokeWidth="0.8" />
                        </g>

                        {/* Eyes — blink */}
                        <g className="puppy-eyes">
                            <ellipse cx="86" cy="94" rx="5" ry="6.5" fill="#2D2A26" />
                            <ellipse cx="114" cy="94" rx="5" ry="6.5" fill="#2D2A26" />
                            {/* Eye shines */}
                            <circle cx="87.5" cy="91.5" r="1.6" fill="#FFFFFF" />
                            <circle cx="115.5" cy="91.5" r="1.6" fill="#FFFFFF" />
                        </g>
                        {/* Eyelids (mask over eyes on blink) */}
                        <g className="puppy-lids">
                            <rect x="80" y="90" width="12" height="9" fill="#F2D6BC" />
                            <rect x="108" y="90" width="12" height="9" fill="#F2D6BC" />
                        </g>

                        {/* Small blush */}
                        <ellipse cx="76" cy="112" rx="4" ry="2.4" fill="#E07A5F" opacity="0.55" />
                        <ellipse cx="124" cy="112" rx="4" ry="2.4" fill="#E07A5F" opacity="0.55" />
                    </g>
                </g>

                {/* Floating hearts on hover / occasional */}
                <g className="puppy-hearts" aria-hidden>
                    <path className="heart h1" d="M0 0 c -3 -3 -8 -1 -8 3 c 0 5 8 8 8 8 c 0 0 8 -3 8 -8 c 0 -4 -5 -6 -8 -3 z"
                          transform="translate(150,72) scale(0.55)" fill="#E07A5F" />
                    <path className="heart h2" d="M0 0 c -3 -3 -8 -1 -8 3 c 0 5 8 8 8 8 c 0 0 8 -3 8 -8 c 0 -4 -5 -6 -8 -3 z"
                          transform="translate(50,64) scale(0.4)" fill="#E07A5F" />
                    <path className="heart h3" d="M0 0 c -3 -3 -8 -1 -8 3 c 0 5 8 8 8 8 c 0 0 8 -3 8 -8 c 0 -4 -5 -6 -8 -3 z"
                          transform="translate(170,110) scale(0.45)" fill="#E07A5F" />
                </g>

                <defs>
                    <radialGradient id="bodyShade" cx="50%" cy="70%" r="60%">
                        <stop offset="0%" stopColor="#F2D6BC" stopOpacity="0" />
                        <stop offset="100%" stopColor="#C9A07A" stopOpacity="0.55" />
                    </radialGradient>
                    <radialGradient id="headShade" cx="50%" cy="70%" r="55%">
                        <stop offset="0%" stopColor="#F2D6BC" stopOpacity="0" />
                        <stop offset="100%" stopColor="#C9A07A" stopOpacity="0.65" />
                    </radialGradient>
                </defs>
            </svg>
        </div>
    );
}
